import { Button, InputText, Select } from "@/deps/ui-library";
import { useAdditionalCharges } from "@/api/queries";
import type { AdditionalCharge, DocumentAdjustment, VatCategory } from "@/api/types";
import { CATEGORY_OPTIONS } from "@/components/LineEditor/LineEditor";
import { useTaxWords } from "@/app/session";

/** An adjustment being edited, with an id so a row keeps its focus while the list changes. */
export type EditableAdjustment = DocumentAdjustment & { id: string };

export const adjustmentIsComplete = (adjustment: DocumentAdjustment) =>
  adjustment.reason.trim() !== "" && Number(adjustment.amount) > 0;

/**
 * Discounts and charges on the whole invoice. Each has its own tax category and needs a reason, which
 * the invoice carries. They go to the sales discount and sales revenue accounts when it posts.
 */
export function DocumentAdjustments({
  adjustments,
  onChange,
  allowDiscounts,
}: {
  adjustments: EditableAdjustment[];
  onChange: (adjustments: EditableAdjustment[]) => void;
  allowDiscounts: boolean;
}) {
  const say = useTaxWords();
  const { data: saved } = useAdditionalCharges();
  // A copy, with the code in the reason, as the design's "Freight (FC)": the invoice keeps what it took.
  const addSaved = (charge: AdditionalCharge) =>
    onChange([
      ...adjustments,
      {
        id: crypto.randomUUID(),
        isCharge: true,
        amount: charge.defaultAmount,
        category: charge.category,
        rate: charge.rate,
        reason: charge.reasonCode ? `${charge.name} (${charge.reasonCode})` : charge.name,
      },
    ]);
  const update = (id: string, patch: Partial<DocumentAdjustment>) =>
    onChange(adjustments.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  const add = (isCharge: boolean) =>
    onChange([
      ...adjustments,
      {
        id: crypto.randomUUID(),
        isCharge,
        amount: "",
        category: "S",
        rate: "13",
        reason: "",
      },
    ]);

  return (
    <div className="flex flex-col gap-2">
      {adjustments.map((adjustment, index) => {
        const name = `${adjustment.isCharge ? "Charge" : "Discount"} ${index + 1}`;
        return (
          <div
            key={adjustment.id}
            className="grid items-end gap-2 md:grid-cols-[2fr_1fr_1fr_6rem_auto]"
          >
            <InputText
              label={`${name} reason`}
              required
              placeholder={adjustment.isCharge ? "e.g. Freight" : "e.g. Early payment"}
              value={adjustment.reason}
              onChange={(event) => update(adjustment.id, { reason: event.target.value })}
            />
            <InputText
              label={`${name} amount`}
              required
              inputMode="decimal"
              className="num"
              value={adjustment.amount}
              onChange={(event) => update(adjustment.id, { amount: event.target.value })}
            />
            <Select
              label={say(`${name} VAT category`)}
              options={CATEGORY_OPTIONS}
              value={adjustment.category}
              showClearButton={false}
              onChange={(details) => {
                const chosen = Array.isArray(details.value) ? details.value[0] : details.value;
                if (!chosen) return;
                const category = String(chosen.value) as VatCategory;
                update(adjustment.id, {
                  category,
                  rate: category === "O" ? null : (adjustment.rate ?? "0"),
                });
              }}
            />
            {adjustment.category === "O" ? (
              <span />
            ) : (
              <InputText
                label={say(`${name} VAT rate`)}
                inputMode="decimal"
                className="num"
                value={adjustment.rate ?? ""}
                onChange={(event) => update(adjustment.id, { rate: event.target.value })}
              />
            )}
            <Button.IconButton
              icon="Trash2"
              variant="ghost"
              colorScheme="default"
              size="sm"
              onClick={() => onChange(adjustments.filter((a) => a.id !== adjustment.id))}
              aria-label={`Remove ${name.toLowerCase()}`}
            />
          </div>
        );
      })}
      <span className="flex flex-wrap items-center gap-2">
        {saved && saved.length > 0 ? (
          <div className="min-w-64">
            <Select
              label="Add a saved charge"
              hideLabel
              placeholder="Add a saved charge"
              value=""
              options={saved.map((charge) => ({ value: charge.id, label: charge.name }))}
              showClearButton={false}
              onChange={(details) => {
                const chosen = Array.isArray(details.value) ? details.value[0] : details.value;
                const charge = saved.find((c) => c.id === String(chosen?.value));
                if (charge) addSaved(charge);
              }}
            />
          </div>
        ) : null}
        <Button colorScheme="default" variant="outline" size="sm" onClick={() => add(true)}>
          Add charge
        </Button>
        {allowDiscounts ? (
          <Button colorScheme="default" variant="outline" size="sm" onClick={() => add(false)}>
            Add discount
          </Button>
        ) : null}
      </span>
    </div>
  );
}
